/**
 * Handlers for updating containers and elements
 */
import type { ContainerElement, TemplateElement, AbsoluteElement } from '../types/template.types'

export interface ElementUpdateHandlers {
  handleContainerChange: (updated: ContainerElement) => void
  handleElementChange: (updated: TemplateElement | AbsoluteElement) => void
}

export interface ElementUpdateState {
  headerContainer: ContainerElement
  footerContainer: ContainerElement
  rootContainer: ContainerElement | ContainerElement[] | null
  absoluteElements: AbsoluteElement[]
  setHeaderContainer: (container: ContainerElement) => void
  setFooterContainer: (container: ContainerElement) => void
  setRootContainer: (container: ContainerElement | ContainerElement[] | null) => void
  setAbsoluteElements: (elements: AbsoluteElement[]) => void
  setHasUnsavedChanges?: (hasChanges: boolean) => void
}

// Update container in tree recursively
const updateContainerById = (
  container: ContainerElement,
  id: string,
  updates: Partial<ContainerElement>
): ContainerElement => {
  if (container.id === id) {
    return { ...container, ...updates }
  }

  const children = Array.isArray(container.children) ? container.children : []
  return {
    ...container,
    children: children.map((child) => ({
      ...child,
      element:
        child.element.type === 'container'
          ? updateContainerById(child.element as ContainerElement, id, updates)
          : child.element,
    })),
  }
}

// Update any element (container or content) in tree recursively
const updateElementById = (
  container: ContainerElement,
  id: string,
  updates: Partial<TemplateElement>
): ContainerElement => {
  if (container.id === id) {
    return { ...container, ...updates } as ContainerElement
  }

  const children = Array.isArray(container.children) ? container.children : []
  return {
    ...container,
    children: children.map((child) => {
      if (child.element.id === id) {
        return {
          ...child,
          element: { ...child.element, ...updates } as TemplateElement,
        }
      }
      if (child.element.type === 'container') {
        return {
          ...child,
          element: updateElementById(child.element as ContainerElement, id, updates),
        }
      }
      return child
    }),
  }
}

// Find any element (container or content) by ID
const findAnyElementById = (
  container: ContainerElement,
  id: string
): TemplateElement | null => {
  if (container.id === id) return container

  const children = Array.isArray(container.children) ? container.children : []
  for (const child of children) {
    if (child.element.id === id) {
      return child.element
    }
    if (child.element.type === 'container') {
      const found = findAnyElementById(child.element as ContainerElement, id)
      if (found) return found
    }
  }
  return null
}

export const createElementUpdateHandlers = (
  state: ElementUpdateState
): ElementUpdateHandlers => {
  const {
    headerContainer,
    footerContainer,
    rootContainer,
    absoluteElements,
    setHeaderContainer,
    setFooterContainer,
    setRootContainer,
    setAbsoluteElements,
    setHasUnsavedChanges,
  } = state

  const handleContainerChange = (updated: ContainerElement) => {
    console.log('handleContainerChange called:', { 
      updatedId: updated.id, 
      updatedType: updated.type,
      headerId: headerContainer.id, 
      footerId: footerContainer.id,
      isHeader: updated.id === headerContainer.id,
      isFooter: updated.id === footerContainer.id
    });
    
    // Check if it's the header container itself
    if (updated.id === headerContainer.id) {
      console.log('Updating header container itself', updated);
      setHeaderContainer(updated)
      setHasUnsavedChanges?.(true)
      return
    }

    // Check if it's the footer container itself
    if (updated.id === footerContainer.id) {
      console.log('Updating footer container itself', updated);
      setFooterContainer(updated)
      setHasUnsavedChanges?.(true)
      return
    }

    // Check if it's a nested container within header
    const inHeader = findAnyElementById(headerContainer, updated.id)
    if (inHeader) {
      console.log('Updating nested container in header', updated);
      const updatedHeader = updateContainerById(headerContainer, updated.id, updated)
      setHeaderContainer(updatedHeader)
      setHasUnsavedChanges?.(true)
      return
    }

    // Check if it's a nested container within footer
    const inFooter = findAnyElementById(footerContainer, updated.id)
    if (inFooter) {
      console.log('Updating nested container in footer', updated);
      const updatedFooter = updateContainerById(footerContainer, updated.id, updated)
      setFooterContainer(updatedFooter)
      setHasUnsavedChanges?.(true)
      return
    }

    console.log('Not header/footer - treating as root container');
    
    // Otherwise it's a root container
    if (!rootContainer) return

    if (Array.isArray(rootContainer)) {
      const newRoot = rootContainer.map(container =>
        updateContainerById(container, updated.id, updated)
      )
      setRootContainer(newRoot)
    } else {
      const newRoot = updateContainerById(rootContainer, updated.id, updated)
      setRootContainer(newRoot)
    }
    setHasUnsavedChanges?.(true)
  }

  const handleElementChange = (updated: TemplateElement | AbsoluteElement) => {
    // Check if it's an absolute element
    if ('type' in updated && updated.type === 'absolute-image') {
      const newElements = absoluteElements.map(el =>
        el.id === updated.id ? updated as AbsoluteElement : el
      )
      setAbsoluteElements(newElements)
      setHasUnsavedChanges?.(true)
      return
    }

    // Check if it's in the header container
    const inHeader = findAnyElementById(headerContainer, updated.id)
    if (inHeader) {
      const updatedHeader = updateElementById(headerContainer, updated.id, updated as TemplateElement)
      setHeaderContainer(updatedHeader)
      setHasUnsavedChanges?.(true)
      return
    }

    // Check if it's in the footer container
    const inFooter = findAnyElementById(footerContainer, updated.id)
    if (inFooter) {
      const updatedFooter = updateElementById(footerContainer, updated.id, updated as TemplateElement)
      setFooterContainer(updatedFooter)
      setHasUnsavedChanges?.(true)
      return
    }

    // Otherwise it's a regular template element in root containers
    if (!rootContainer) return

    if (Array.isArray(rootContainer)) {
      const newRoot = rootContainer.map(container =>
        updateElementById(container, updated.id, updated as TemplateElement)
      )
      setRootContainer(newRoot)
    } else {
      const newRoot = updateElementById(rootContainer, updated.id, updated as TemplateElement)
      setRootContainer(newRoot)
    }
    setHasUnsavedChanges?.(true)
  }

  return {
    handleContainerChange,
    handleElementChange,
  }
}
